import type { ComponentType } from 'react';
import {
  Home20Regular,
  Home20Filled,
  Bot20Regular,
  Bot20Filled,
  Flow20Regular,
  Flow20Filled,
  PlugConnected20Regular,
  PlugConnected20Filled,
  SlideSettings20Regular,
  SlideSettings20Filled,
  DataUsage20Regular,
  DataUsage20Filled,
  Settings20Regular,
  Settings20Filled,
} from '@fluentui/react-icons';
import type { AppPermissionLevel } from '@/hooks/useAppPermission';

export interface AppNavItem {
  id: string;
  label: string;
  icon: ComponentType;
  activeIcon: ComponentType;
  /** Relative path from the app layout (e.g., './workflows', './connections') */
  path: string;
  /** If true, NavLink uses exact (`end`) matching — required for `/` style absolute home links. */
  end?: boolean;
  /** Minimum permission level required to access this page. Defaults to 'Contributor'. */
  requiredPermission?: AppPermissionLevel;
}

export interface AppNavSection {
  id: string;
  /** When omitted, the section renders unlabelled and without a divider. */
  label?: string;
  items: AppNavItem[];
}

/**
 * App-scoped nav sections.
 */
export function getAppNavSections(): AppNavSection[] {
  return [
    {
      id: 'home',
      // No label — Home (and the link back to the parent project) sit at the
      // top as primary destinations. AppLayout injects the Project link at
      // runtime so it can use the live project name + tenant-aware href.
      items: [
        { id: 'home', label: 'Home', icon: Home20Regular, activeIcon: Home20Filled, path: '/', end: true, requiredPermission: 'Reader' },
      ],
    },
    // Single app-scoped section. AppLayout overrides `label` with the
    // current app's name and supplies an icon at runtime — the static
    // "Application" label here is only the fallback used before the app loads.
    {
      id: 'application',
      label: 'Application',
      items: [
        { id: 'workflows', label: 'Workflows', icon: Flow20Regular, activeIcon: Flow20Filled, path: './workflows', requiredPermission: 'Reader' },
        { id: 'agents', label: 'Agents', icon: Bot20Regular, activeIcon: Bot20Filled, path: './agents', requiredPermission: 'Reader' },
        { id: 'connections', label: 'Connections', icon: PlugConnected20Regular, activeIcon: PlugConnected20Filled, path: './connections', requiredPermission: 'Reader' },
        { id: 'parameters', label: 'Parameters', icon: SlideSettings20Regular, activeIcon: SlideSettings20Filled, path: './parameters', requiredPermission: 'Reader' },
        { id: 'analytics', label: 'Analytics', icon: DataUsage20Regular, activeIcon: DataUsage20Filled, path: './analytics', requiredPermission: 'Reader' },
        { id: 'settings', label: 'Settings', icon: Settings20Regular, activeIcon: Settings20Filled, path: './settings', requiredPermission: 'Reader' },
      ],
    },
  ];
}

/**
 * Check whether a user's permission level meets the item's requirement.
 * Currently only distinguishes Reader (view-only) from Contributor/Author (full access).
 * If finer-grained levels are needed later, extend the hierarchy here.
 */
export function hasNavPermission(
  item: AppNavItem,
  userPermission: AppPermissionLevel,
): boolean {
  const required = item.requiredPermission ?? 'Contributor';
  if (!userPermission) return false;
  if (required === 'Reader') return true;
  return userPermission === 'Contributor' || userPermission === 'Owner';
}

/** Flat list derived from sections — backward compat. Call during render. */
export function getAppNavItems(): AppNavItem[] {
  return getAppNavSections().flatMap((s) => s.items);
}
